/**
 * SmartReplyChips — Send-form 上部に表示する 3 候補返信案 chip 群.
 *
 * 設計書: docs/chatwork_killer/07_ai_voice_video.md セクション 5
 *
 * 振る舞い:
 *   - props.context が変わったら、デバウンス後に POST /v1/messages/:id/smart-reply を叩く.
 *   - 3 候補 (polite / casual / concise) を chip として表示.
 *   - chip クリックで onSelect(candidate.body) を呼び、同時に
 *     POST /v1/messages/:id/smart-reply/adopt で「採用」を学習データへ送る.
 *   - SLO 800ms を超えたら "AI 候補生成中…" を表示 (キャンセルボタンも提示).
 *   - エラー時は「候補を生成できませんでした」を出す (UI を破壊しない).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import {
  fetchSmartReply,
  adoptSmartReply,
  type SmartReplyCandidate,
  type SmartReplyContextMessage,
  type SmartReplyTone,
} from "./api";
import { cn } from "@/lib/cn";

const TONE_LABEL_JA: Record<SmartReplyTone, string> = {
  polite: "丁寧",
  casual: "カジュアル",
  concise: "簡潔",
};

const TONE_LABEL_EN: Record<SmartReplyTone, string> = {
  polite: "Polite",
  casual: "Casual",
  concise: "Concise",
};

const TONE_BADGE_BG: Record<SmartReplyTone, string> = {
  polite: "bg-brand-100 text-brand-700",
  casual: "bg-success-bg text-success",
  concise: "bg-warning-bg text-warning",
};

export interface SmartReplyChipsProps {
  /** 返信対象の message_id (末尾メッセージ) */
  messageId: string;
  /** 直近 10〜20 件の文脈. 末尾が target. */
  context: SmartReplyContextMessage[];
  /** 候補を選択したときに送信フォームへ流し込む */
  onSelect: (body: string) => void;
  /** チップの非表示 (送信中など) */
  hidden?: boolean;
  /** UI ロケール */
  locale?: "ja-JP" | "en-US";
  /** デバウンス待ち時間 (ms). 既定 600. */
  debounceMs?: number;
  /** テスト用 fetch 差し替え */
  fetcher?: typeof fetchSmartReply;
  adopter?: typeof adoptSmartReply;
}

interface State {
  status: "idle" | "loading" | "ready" | "error";
  candidates: SmartReplyCandidate[];
  error?: string;
  latencyMs?: number;
}

export function SmartReplyChips(props: SmartReplyChipsProps): JSX.Element | null {
  const { t } = useTranslation();
  const {
    messageId,
    context,
    onSelect,
    hidden,
    locale = "ja-JP",
    debounceMs = 600,
    fetcher = fetchSmartReply,
    adopter = adoptSmartReply,
  } = props;
  const [state, setState] = useState<State>({ status: "idle", candidates: [] });
  const [dismissed, setDismissed] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const labels = locale === "ja-JP" ? TONE_LABEL_JA : TONE_LABEL_EN;

  // context / messageId が変わったらフェッチを走らせる. Debounce で連打を防ぐ.
  useEffect(() => {
    if (hidden || dismissed) return;
    if (!messageId || context.length === 0) {
      setState({ status: "idle", candidates: [] });
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const ctrl = new AbortController();
      abortRef.current?.abort();
      abortRef.current = ctrl;
      setState({ status: "loading", candidates: [] });
      fetcher(messageId, { context, locale })
        .then((res) => {
          if (ctrl.signal.aborted) return;
          setState({
            status: "ready",
            candidates: res.candidates,
            latencyMs: res.latency_ms,
          });
        })
        .catch((e: unknown) => {
          if (ctrl.signal.aborted) return;
          const msg = e instanceof Error ? e.message : "smart-reply failed";
          setState({ status: "error", candidates: [], error: msg });
        });
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [messageId, context, locale, hidden, dismissed, debounceMs, fetcher]);

  if (hidden || dismissed) return null;
  if (state.status === "idle" && state.candidates.length === 0) return null;

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2",
        "border-t border-border-subtle bg-surface-base",
        "overflow-x-auto",
      )}
      role="group"
      aria-label={t("ai.smartReply.label", { defaultValue: "AI 返信案" }) as string}
      data-testid="smart-reply-chips"
    >
      <Sparkles
        size={14}
        aria-hidden
        className="text-accent-ai shrink-0"
      />
      {state.status === "loading" && (
        <span className="text-xs text-fg-muted shrink-0" aria-live="polite">
          {t("ai.smartReply.loading", { defaultValue: "返信案を生成中..." }) as string}
        </span>
      )}
      {state.status === "error" && (
        <span className="text-xs text-danger shrink-0" aria-live="polite">
          {t("ai.smartReply.error", { defaultValue: "候補を生成できませんでした" }) as string}
        </span>
      )}
      {state.status === "ready" &&
        state.candidates.map((c) => (
          <button
            type="button"
            key={`${c.tone}-${c.body.slice(0, 16)}`}
            onClick={() => {
              onSelect(c.body);
              // adopt は best-effort. 失敗しても UI は止めない.
              void adopter(messageId, c).catch(() => undefined);
            }}
            className={cn(
              "shrink-0 inline-flex items-center gap-2",
              "px-3 py-1.5 rounded-full",
              "border border-border-subtle bg-canvas",
              "text-sm text-fg-primary",
              "hover:bg-surface-raised active:bg-surface-sunken",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600",
            )}
            data-tone={c.tone}
            data-testid={`smart-reply-chip-${c.tone}`}
            aria-label={`${labels[c.tone]}: ${c.body}`}
          >
            <span
              className={cn(
                "px-1.5 py-0.5 rounded text-xs font-medium",
                TONE_BADGE_BG[c.tone],
              )}
            >
              {labels[c.tone]}
            </span>
            <span className="truncate max-w-[420px]">{c.body}</span>
          </button>
        ))}
      <span className="flex-1" />
      <button
        type="button"
        aria-label={t("ai.smartReply.dismiss", { defaultValue: "閉じる" }) as string}
        onClick={() => setDismissed(true)}
        className="p-1 rounded text-fg-muted hover:bg-surface-raised shrink-0"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}

/** test 用 export */
export const _internal = { TONE_LABEL_JA, TONE_LABEL_EN, TONE_BADGE_BG };
